/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */

import { db } from './firebase';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import { UserAuthRecord, UserRole } from '../types/auth';
import { User } from '../types';

let _cachedSSOT: UserAuthRecord | null = null;

export function setStoredSSOTAuthDoc(record: UserAuthRecord | null) {
  _cachedSSOT = record;
}

export function getStoredSSOTAuthDoc(): UserAuthRecord | null {
  return _cachedSSOT;
}

/**
 * Ensures authoritative UserAuthRecord exists in /insumedic_user_auth/{firebaseUid}
 * If not present, creates it strictly according to the authorized master catalogue.
 */
export async function getOrCreateAuthoritativeUserAuth(
  firebaseUid: string, 
  email?: string | null,
  initialUserMatch?: User | null
): Promise<UserAuthRecord> {
  const authDocRef = doc(db, 'insumedic_user_auth', firebaseUid);
  
  try {
    const snap = await getDoc(authDocRef);
    if (snap.exists()) {
      const data = snap.data() as UserAuthRecord;
      _cachedSSOT = data;
      return data;
    }
  } catch (err) {
    console.warn('Error reading authoritative auth document:', err);
  }

  // Derive initial authoritative assignment
  let role: UserRole = 'director_tecnico';
  let legacyUserId = initialUserMatch?.id || 'user-administrador-unique';
  let companyId = initialUserMatch?.companyId || 'company-insumedic';

  const isMasterSuperAdmin = 
    (email && email.toLowerCase() === 'qualityprocesosyservicios@gmail.com') ||
    (initialUserMatch && initialUserMatch.username?.toUpperCase() === 'JUAN.VALENCIA');

  if (isMasterSuperAdmin) {
    role = 'superadmin';
  } else if (initialUserMatch) {
    if (initialUserMatch.esAdmin || initialUserMatch.cargo?.toLowerCase().includes('director') || initialUserMatch.cargo?.toLowerCase().includes('administrador')) {
      role = 'director_tecnico';
    } else if (initialUserMatch.cargo?.toLowerCase().includes('regente')) {
      role = 'regente';
    } else if (initialUserMatch.cargo?.toLowerCase().includes('calidad')) {
      role = 'calidad';
    } else if (initialUserMatch.cargo?.toLowerCase().includes('operario') || initialUserMatch.cargo?.toLowerCase().includes('auxiliar')) {
      role = 'operario';
    }
  }

  const newAuthRecord: UserAuthRecord = {
    uid: firebaseUid,
    legacyUserId,
    companyId,
    role,
    estado: 'ACTIVO',
    updatedAt: new Date().toISOString(),
    authorizationVersion: 1
  };

  if (email) {
    newAuthRecord.email = email;
  }

  try {
    await setDoc(authDocRef, newAuthRecord);
  } catch (writeErr) {
    console.warn('Could not write initial auth record to cloud:', writeErr);
  }

  _cachedSSOT = newAuthRecord;
  return newAuthRecord;
}
